import classNames from 'classnames'
import { Utils } from 'common/utils'
import { useRouter } from 'next/router'
import { NextSeoProps } from 'next-seo'

import { getPlans } from 'common/planData'
import { createClient } from '../prismicio'
import { PlanSlug } from 'common/planSlugs'
import Button from 'components/forms/Button'
import { ButtonType } from '../types/global'
import WhyChoose from 'components/WhyChoose'
import FAQList from 'components/util/FAQList'
import { planPrice } from 'common/utils/planPrice'
import useShoppingBag from 'common/useShoppingBag'
import HeroBanner from 'components/hero/HeroBanner'
import DemoCard from 'components/cards/new/DemoCard'
import { heroGap, sectionGap } from 'project/padding'
import HeadingAndText from 'components/HeadingAndText'
import { getFAQList, getReviews } from 'common/cms-sdk'
import standardiseTitle from 'project/standardiseTitle'
import useWindowSize from 'components/util/useWindowSize'
import { I18NConfig } from 'next/dist/server/config-shared'
import ContactUsCard from 'components/cards/new/ContactUsCard'
import TidePromoCard from 'components/cards/new/TidePromoCard'
import ReviewsSection from 'components/sections/ReviewsSection'
import { NextPageWithLayout } from '../types/nextPageWithLayout'
import SubscriptionPricing from 'components/SubscriptionPricing'
import SeoContainer from 'components/util/containers/SeoContainer'
import WorkFromAnywhere from 'components/cards/new/WorkFromAnywhere'
import { TAddon, TFAQList, TPlanSummary, TReviews } from 'common/cms-types'
import { virtualOfficeStrings } from 'common/strings/virtual-office-strings'
import WhatIsMailForwarding from 'components/cards/new/WhatIsMailForwarding'
import EmbededNewsletterForm from 'components/sections/EmbededNewsletterForm'
import MailForwardingOptions from 'components/cards/new/MailForwardingOptions'
import MailForwardingDetails from 'components/cards/new/MailForwardingDetails'
import { virtualOfficeOldStreetBreadcrumb } from 'components/util/containers/Breadcrumbs'

export type VOForwardType = {
  // @ts-ignore
  plans: TPlanSummary[]
  addons: TAddon[] | null
  reviews: TReviews
  faqList: TFAQList
}
const VOForwardPage: NextPageWithLayout<VOForwardType> = ({
  faqList,
  plans,
  reviews,
}) => {
  const router = useRouter()
  const size = useWindowSize()
  const breadcrumb = virtualOfficeOldStreetBreadcrumb().concat([
    {
      item: `https://www.hoxtonmix.com/virtual-office-forward/`,
      name: `Virtual Office Forward`,
      position: 3,
    },
  ])
  const { addPlan } = useShoppingBag()
  const planSummary = plans.find((v) => v.slug === PlanSlug.SCAN_PRO)!
  const onBuyClick = () => {
    addPlan(
      planSummary.slug,
      'year',
      planSummary,
      ButtonType.VIRTUAL_OFFICE_FORWARD_PAGE_HERO,
    )
    router.push('/checkout')
  }

  const seoProps: NextSeoProps = {
    description: `Use our exclusive London address as your business mailing address with mail handling and forwarding within one business day.`,
    openGraph: {
      url: `https://www.hoxtonmix.com/virtual-office-forward/`,
    },
    title: standardiseTitle(
      `Business Mail Forwarding Services in London, UK - Hoxton Mix`,
    ),
  }
  return (
    <SeoContainer breadcrumb={breadcrumb} seoProps={seoProps}>
      <main
        className={classNames(
          'overflow-x-hidden new-design d-flex flex-column',
          heroGap,
        )}
      >
        <HeroBanner
          robotImage={{
            alt: 'HaiRo™ robot mascot pointing at envelopes',
            dimensions: {
              height: 931,
              width: 703,
            },
            src: '/images/new/robot-pointing-mails.png',
          }}
          title='Mail forwarding add-on for our London virtual office plan.'
          cta={
            <SubscriptionPricing
              buttonType={ButtonType.VIRTUAL_OFFICE_FORWARD_PAGE_HERO}
              hideTitle
              buttonAlign='start'
              planSummary={plans.find((v) => v.slug === PlanSlug.SCAN_PRO)!}
            />
          }
          subtitle={
            <span className='fs-fluid-24-48'>
              <span className='fw-semibold'>
                Add a{' '}
                <span className='color-pink2 fw-semibold'>
                  UK Mail Forwarding within one business day
                </span>{' '}
                service to your plan for{' '}
                {planPrice
                  .monthlyOnYearlyPlan(`${PlanSlug.SCAN_PRO}-addon`)
                  .toLowerCase()}
                .
              </span>
            </span>
          }
          description={
            <span className='fw-semibold fs-fluid-24-26 pe-lg-10'>
              Get started with your virtual office services with mail
              forwarding.
            </span>
          }
        />
        <div
          className={classNames(
            'container d-flex flex-column gap-4',
            sectionGap,
          )}
        >
          <div className='d-flex flex-column text-center gap-4 fw-semibold'>
            <h2 className='fs-fluid-36-80 lh-105 fw-semibold whitespace-nowrap'>
              If you need to have all of your{' '}
              <br className='d-none d-lg-block' /> physical post sent to you, we{' '}
              <br className='d-none d-lg-block' /> have got you covered.
            </h2>
            <span
              className='lh-119 fs-fluid-20-24 mx-auto'
              style={{
                maxWidth: 1000,
              }}
            >
              This add-on is specifically for those people who absolutely need
              to have their physical post all of the time. With the mail
              forwarding add-on we still scan and digitise your physical mail,
              but then we forward it to your chosen address. All within the same
              business day. With the mail forwarding add-on, there are no limits
              and you can cancel your plan from month-to-month.
            </span>
            <span className='lh-114 fs-fluid-20-26'>
              Just add it to your basket at check out.
            </span>
            <Button
              onClick={onBuyClick}
              className='w-100 w-lg-auto align-self-lg-center'
            >
              Buy Now
            </Button>
          </div>
          <WhatIsMailForwarding />
          <MailForwardingOptions planSummary={planSummary} />
          <div>
            <HeadingAndText
              classesForTitle='fs-fluid-36-80 lh-105'
              headingSize={2}
              title={virtualOfficeStrings.whyChooseUsForward}
              subtitle=''
            />
            <div className='d-flex justify-content-md-center mt-lg-10 mt-6 px-lg-6'>
              <div className='col-lg-9 col-12 pt-8'>
                <WhyChoose isForward />
              </div>
            </div>
          </div>
          <div className={classNames('d-flex flex-column', heroGap)}>
            <DemoCard />
            <div className='text-center'>
              <SubscriptionPricing
                buttonType={ButtonType.VIRTUAL_OFFICE_FORWARD_DEMO_CTA}
                planSummary={plans.find((v) => v.slug === PlanSlug.SCAN_PRO)!}
              />
            </div>
          </div>
          <MailForwardingDetails planSummary={planSummary} />
          <WorkFromAnywhere planSummary={planSummary} />
          <FAQList
            title={faqList.data.title}
            subtitle={faqList.data.subtitle}
            slices={faqList.data.slices}
            expandAllButton
            expandAllButtonDesktopOnly
          />
          <div className='row gy-5'>
            <div className='col-12 col-lg-6 '>
              <ContactUsCard />
            </div>
            <div className='col-12 col-lg-6'>
              <TidePromoCard />
            </div>
          </div>
          <EmbededNewsletterForm />
        </div>
        <ReviewsSection
          data={reviews}
          plan={PlanSlug.SCAN_PRO}
          pageUrl={router.pathname}
        />
      </main>
    </SeoContainer>
  )
}

export async function getStaticProps(
  context: I18NConfig & { locale?: string; params: { uid: string } },
) {
  try {
    const client = createClient(context)
    const lang = Utils.getLocaleID(context.locale || context.defaultLocale)

    const plans = await getPlans()
    const reviews = await getReviews()
    const faqList = await getFAQList(client, lang, 'virtual-office-forward')

    return {
      props: {
        faqList,
        plans,
        reviews,
      },
    }
  } catch (e) {
    console.log(e)
    return {
      props: {},
    }
  }
}

export default VOForwardPage
